<template>
  <transition name="fade">
    <div v-if="modelValue" class="fixed inset-0 z-50 flex flex-col bg-white">
      <ConfirmationModal
        :show="showConfirm"
        title="Confirm Action"
        :message="`Are you sure you want to ${isEdit ? 'update' : 'upload'} ${items.length} file(s)?`"
        confirmText="Yes"
        cancelText="Cancel"
        :loading="loading"
        @confirm="handleConfirmUpload"
        @cancel="showConfirm = false"
      />

      <!-- Header -->
      <div class="flex justify-between items-center p-4 border-b border-gray-200 bg-orange-400">
        <h2 class="text-xl font-semibold text-text-primary">
          {{ isEdit ? "Edit MOA" : "Upload MOA" }}
        </h2>
        <button @click="close" class="text-gray-500 hover:text-gray-700 text-3xl font-bold">&times;</button>
      </div>

      <!-- Body -->
      <div class="flex flex-col md:flex-row flex-1 overflow-hidden">
        <!-- LEFT: Dropzone -->
        <div class="md:w-1/2 flex flex-col items-center justify-center p-8 border-b md:border-b-0 md:border-r border-gray-200">
          <p class="text-sm text-text-secondary mb-4 text-center">Drag & drop or click to select files (PDF, XLSX, DOCX)</p>

          <div
            @click="triggerFileInput"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
            :class="[
              'border-2 border-dashed rounded-lg p-12 flex flex-col items-center justify-center cursor-pointer transition-colors w-full max-w-md',
              isDragging ? 'border-primary bg-primary-light-header' : 'border-gray-300 bg-white',
            ]"
          >
            <div class="text-sm text-text-primary text-center">
              <span v-if="!items.length"> Click or drag & drop to {{ isEdit ? "replace" : "upload" }} </span>
              <span v-else class="font-medium"> {{ items.length }} file(s) selected </span>
            </div>

            <div class="text-xs text-gray-400 mt-2">Max 20MB each</div>

            <input type="file" ref="fileInputRef" class="hidden" multiple @change="onFileChange" />
          </div>
        </div>

        <!-- RIGHT: File Cards -->
        <div class="md:w-1/2 p-8 flex flex-col flex-1">
          <h3 class="text-lg font-semibold text-text-primary mb-3">
            {{ isEdit ? "MOA Details" : "Files to Upload" }}
          </h3>

          <div class="flex-1 overflow-y-auto space-y-3">
            <div v-for="(item, index) in items" :key="index" :class="['border rounded-lg p-3 flex flex-col gap-2', index % 2 === 0 ? 'bg-white' : 'bg-gray-50']">
              <div class="flex justify-between items-center">
                <span class="truncate font-medium">
                  {{ item.file?.name || item.filename }}
                </span>

                <button @click="removeItem(index)" class="text-red-500 text-sm hover:underline">Remove</button>
              </div>
            </div>
          </div>

          <button
            :disabled="!items.length || loading"
            @click="showConfirm = true"
            class="mt-4 w-full bg-primary-dark text-white py-3 rounded-lg font-medium disabled:opacity-60 disabled:cursor-not-allowed hover:bg-primary-dark"
          >
            <span v-if="loading">
              {{ isEdit ? "Updating..." : "Uploading..." }}
            </span>

            <span v-else>
              {{ isEdit ? "Update MOA" : "Upload & Continue" }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, watch } from "vue";
import { useRoute } from "vue-router";
import { createSignedMoa, updateSignedMoa } from "@/api/signedMoa";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";

export default {
  props: {
    modelValue: Boolean,
    isEdit: Boolean,
    productData: Object,
  },

  components: { ConfirmationModal },

  emits: ["update:modelValue", "save"],

  setup(props, { emit }) {
    const route = useRoute();

    const fileInputRef = ref(null);
    const isDragging = ref(false);
    const showConfirm = ref(false);
    const loading = ref(false);

    const items = ref([]);

    const getCompanyFromFilename = (filename) => {
      return filename.replace(/\.[^/.]+$/, ""); // remove extension
    };

    watch(
      () => props.productData,
      (data) => {
        if (props.isEdit && data) {
          items.value = [
            {
              id: data.id,
              filename: data.filename,
              company: getCompanyFromFilename(data.filename),
              file: null,
            },
          ];
        } else {
          items.value = [];
        }
      },
      { immediate: true },
    );

    const triggerFileInput = () => fileInputRef.value?.click();

    const onFileChange = (e) => {
      const files = Array.from(e.target.files).map((file) => ({
        file,
        filename: file.name,
        company: getCompanyFromFilename(file.name),
      }));

      items.value = props.isEdit ? [{ ...items.value[0], ...files[0] }] : [...items.value, ...files];

      e.target.value = "";
    };

    const handleDrop = (e) => {
      isDragging.value = false;

      const files = Array.from(e.dataTransfer.files).map((file) => ({
        file,
        filename: file.name,
        company: getCompanyFromFilename(file.name),
      }));

      items.value = props.isEdit ? [{ ...items.value[0], ...files[0] }] : [...items.value, ...files];
    };

    const removeItem = (index) => {
      items.value.splice(index, 1);
    };

    const close = () => emit("update:modelValue", false);

    const handleConfirmUpload = async () => {
      showConfirm.value = false;
      await submit();
    };

    const submit = async () => {
      const section_id = route.query.sectionId;

      if (!section_id) {
        alert("section_id is missing in the URL.");
        return;
      }

      const formData = new FormData();
      formData.append("section_id", section_id);

      items.value.forEach((item) => {
        if (item.file) formData.append(props.isEdit ? "file" : "files", item.file);

        formData.append(props.isEdit ? "company" : "companies[]", item.company);
      });

      loading.value = true;

      try {
        if (props.isEdit && items.value[0].id) {
          await updateSignedMoa(items.value[0].id, formData);
        } else {
          await createSignedMoa(formData);
        }

        items.value = [];
        emit("save");
        emit("update:modelValue", false);
      } catch (err) {
        console.error(err.response);
        alert("Error saving MOA.");
      } finally {
        loading.value = false;
      }
    };

    return {
      items,
      fileInputRef,
      isDragging,
      showConfirm,
      loading,
      triggerFileInput,
      onFileChange,
      handleDrop,
      removeItem,
      handleConfirmUpload,
      close,
    };
  },
};
</script>
